import React, { useState } from "react";
import { CheckCircle2, RotateCcw, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const num = (value) => (Number(value) || 0).toLocaleString("id-ID", {
  maximumFractionDigits: 2,
});

export const FabricOrphanRecoveryDialog = ({ color, onClose, onRecovered }) => {
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const requestPreview = async () => {
    if (!reason.trim()) {
      toast.error("Alasan pemulihan wajib diisi");
      return;
    }
    setBusy(true);
    try {
      const response = await api.post(
        `/production/fabrics/color/${color.id}/recover-orphan-cutting`,
        { reason: reason.trim(), confirm: false },
      );
      setPreview(response.data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmRecovery = async () => {
    setBusy(true);
    try {
      const response = await api.post(
        `/production/fabrics/color/${color.id}/recover-orphan-cutting`,
        { reason: reason.trim(), confirm: true },
      );
      toast.success(`${num(response.data.total_yard)} yd kembali ke Available`);
      await onRecovered();
      onClose();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="fabric-orphan-recovery-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cocoa">
            <RotateCcw className="h-5 w-5" />
            Pemulihan Orphan Cutting
          </DialogTitle>
          <DialogDescription>
            {color.fabricName} · {color.colorName}
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="space-y-4">
            <div className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="flex items-center gap-2 font-semibold">
                <TriangleAlert className="h-4 w-4" />
                Stok akan dipindahkan dari Cutting ke Available pada roll dan gudang yang sama.
              </div>
              <p className="mt-1 text-xs">
                Dokumen Cutting tidak dibuat atau dihapus sebelumnya, sehingga tidak dapat dibatalkan
                dari daftar Cutting.
              </p>
            </div>
            <div>
              <Label htmlFor="orphan-cutting-recovery-reason">Alasan pemulihan</Label>
              <Input
                data-testid="orphan-cutting-recovery-reason"
                id="orphan-cutting-recovery-reason"
                onChange={(event) => setReason(event.target.value)}
                placeholder="Contoh: Cutting lama tidak memiliki dokumen"
                value={reason}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="orphan-cutting-recovery-preview">
            <div className="border-l-4 border-cocoa bg-ivory p-3 text-sm text-cocoa">
              <div className="font-semibold">
                {num(preview.total_yard)} yd akan dipulihkan ke Available
              </div>
              <div className="mt-1 text-xs">Alasan: {reason}</div>
            </div>
            {preview.rows.map((row) => (
              <div
                className="flex items-center justify-between border-b border-taupe/20 py-2 text-sm"
                data-testid={`orphan-cutting-recovery-row-${row.warehouse_id}-${row.lot}`}
                key={`${row.warehouse_id}-${row.lot}`}
              >
                <span className="text-wmuted">Gudang {row.warehouse_id} · Lot {row.lot || "-"}</span>
                <span className="font-semibold text-cocoa">{num(row.qty)} yd</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            data-testid="orphan-cutting-recovery-cancel"
            disabled={busy}
            onClick={onClose}
            variant="outline"
          >
            Batal
          </Button>
          {!preview ? (
            <Button
              data-testid="orphan-cutting-recovery-preview-button"
              disabled={busy}
              onClick={requestPreview}
            >
              {busy ? "Memeriksa..." : "Lihat Preview"}
            </Button>
          ) : (
            <Button
              data-testid="orphan-cutting-recovery-confirm"
              disabled={busy}
              onClick={confirmRecovery}
            >
              <CheckCircle2 className="mr-1.5 h-4 w-4" />
              {busy ? "Memulihkan..." : "Konfirmasi Pemulihan"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};